import {
  FormCheckbox,
  FormSelect,
  EnumValueDisplay,
  FormTextField,
  FormNumberField,
  FormTimePicker,
  Feedback,
  ErrorDisplay,
  useFormEvents,
} from '@lib/common';
import { CorporateAccount, FormStatus, hasErrorCode } from '@lib/state';
import { BillingFrequencyType, PaymentTerm } from '@lib/state/api/generated/reservations';
import { Grid, Typography, MenuItem, Box } from '@material-ui/core';
import { CorporateAccountForms } from 'app/state';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface Props {
  account: CorporateAccount;
}

// Matches AutoNoShowCutoffExceededException.ErrorCodeValue. The server owns the
// timezone-dependent half of the cap (see AUTO_NO_SHOW_TIME_VALIDATION below), so its rejection
// has to reach this field rather than only the page-level error banner.
const AUTO_NO_SHOW_CUTOFF_ERROR_CODE = 'AUTO_NO_SHOW_CUTOFF_EXCEEDED';

const AUTO_NO_SHOW_CUTOFF_NOTE =
  'Cutoff cannot exceed 12:00 PM property-local time or 12:00 PM Mountain Time ' +
  '(whichever is earlier), so the no-show status flip completes before the Daily ' +
  'Housing Report and Daily Occupancy Report emails are sent at 2:00 PM Central Time.';

// Deliberately checks only the timezone-INDEPENDENT half of the cap: a wall clock past 12:00
// is invalid for every property, so it can be rejected instantly without knowing which property
// applies. The other half (12:00 America/Denver, in absolute time) depends on the property's UTC
// offset and on the season, and is enforced server-side by AutoNoShowCutoffCap. Porting that here
// would mean a second DST-aware implementation to keep in sync — the Phoenix case, where 12:00 is
// legal in January and over cap in July, shows how quietly the two would drift. The server's
// rejection surfaces on this same field via AUTO_NO_SHOW_CUTOFF_ERROR_CODE.
//
// Stable identity — inline `{{ validate: ... }}` breaks react-hook-form v5's
// register/unregister effect and wipes the field value on re-render.
const AUTO_NO_SHOW_TIME_VALIDATION = {
  validate: (value: string) => {
    if (!value) return true;
    const [hoursStr, minutesStr] = value.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr ?? '0', 10);
    if (Number.isNaN(hours)) return true;
    if (hours > 12 || (hours === 12 && minutes > 0)) {
      return AUTO_NO_SHOW_CUTOFF_NOTE;
    }
    return true;
  },
};

export function BillingPolicyForm({ account }: Props) {
  const [{ status, error }, reset] = useFormEvents(CorporateAccountForms.UpdateBillingSettings);

  // A cutoff rejection belongs on the field that caused it, not in the page-level banner, so it
  // is routed to the field note and withheld from ErrorDisplay to avoid showing it twice.
  const cutoffError = hasErrorCode(error, AUTO_NO_SHOW_CUTOFF_ERROR_CODE) ? error : undefined;
  const { setValue, errors, watch } = useFormContext();

  const [showTaxCreditSplit, setShowTaxCreditSplit] = useState(false);
  const [propertyTaxCreditSplitValue, setPropertyTaxCreditSplitValue] = useState(
    account?.taxCreditSplit
  );

  const needsReview = watch('invoiceNeedsReview', account?.needsReview);
  const billingFrequencyType = watch('billingFrequencyType', account?.billingFrequencyType);
  const allowTaxCredit = watch('allowTaxCredit', false);
  const directBillingEnabled = watch('isInvoiceEnabled', account?.isInvoiceEnabled);

  const billingFrequencies = useMemo(
    () =>
      Object.values(BillingFrequencyType).map(b => ({
        billingFrequencyType: b,
        disabled:
          (directBillingEnabled && b === BillingFrequencyType.NA) ||
          (needsReview &&
            b !== BillingFrequencyType.Weekly &&
            b !== BillingFrequencyType.SemiMonthly) ||
          (b === BillingFrequencyType.CreditCard && directBillingEnabled),
      })),
    [needsReview, directBillingEnabled]
  );

  useEffect(() => {
    setShowTaxCreditSplit(allowTaxCredit === true);
  }, [allowTaxCredit, setShowTaxCreditSplit]);

  useEffect(() => {
    if (directBillingEnabled) {
      setValue(
        'billingFrequencyType',
        account?.billingFrequencyType !== BillingFrequencyType.NA
          ? account?.billingFrequencyType
          : BillingFrequencyType.Weekly
      );
    } else {
      setValue('invoiceNeedsReview', false);
      if (account?.billingFrequencyType !== BillingFrequencyType.CreditCard) {
        setValue('billingFrequencyType', BillingFrequencyType.NA);
      }
    }
  }, [directBillingEnabled, account, setValue]);

  useEffect(() => {
    if (
      needsReview &&
      billingFrequencyType !== BillingFrequencyType.NA &&
      billingFrequencyType !== BillingFrequencyType.Weekly &&
      billingFrequencyType !== BillingFrequencyType.SemiMonthly
    ) {
      setValue('billingFrequencyType', BillingFrequencyType.Weekly);
    }
  }, [needsReview, billingFrequencyType, setValue]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item md={6} xs={12}>
          <FormCheckbox
            name="isInvoiceEnabled"
            label="Direct Billing"
            defaultChecked={account.isInvoiceEnabled}
            data-testid="corporateInvoiceCheckbox"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormCheckbox
            name="invoiceNeedsReview"
            label="Invoice Needs Review"
            defaultChecked={account.needsReview}
            disabled={!directBillingEnabled}
            data-testid="corporateInvoiceNeedsReviewCheckbox"
          />
        </Grid>

        <Grid item md={6} xs={12} style={{ marginTop: '-0.7em' }}>
          <Typography variant="caption" style={{ color: 'gray' }}>
            {errors.isInvoiceEnabled?.message ??
              'If enabled, then the option to use an Invoice Payment method is available when booking.'}
          </Typography>
        </Grid>
        <Grid item md={6} xs={12} style={{ marginTop: '-0.7em' }}>
          <Typography variant="caption" style={{ color: 'gray' }}>
            {
              'If enabled, then this corporate account needs invoices to be reviewed prior to sending.'
            }
          </Typography>
        </Grid>
        <Grid item md={6} xs={12}>
          <FormSelect
            defaultValue={account.billingFrequencyType}
            name="billingFrequencyType"
            label="Billing Frequency"
            fullWidth
          >
            {billingFrequencies.map(({ billingFrequencyType, disabled }) => (
              <MenuItem
                key={billingFrequencyType}
                value={billingFrequencyType}
                dense
                disabled={disabled}
              >
                <EnumValueDisplay value={billingFrequencyType} disableTypography />
              </MenuItem>
            ))}
          </FormSelect>
        </Grid>
        <Grid item md={3} xs={12}>
          <FormSelect
            name="paymentTerm"
            label="Payment term"
            fullWidth
            defaultValue={account?.paymentTerm}
            data-testid="corporatePaymentTermSelect"
          >
            <MenuItem value={PaymentTerm.DueOnReceipt} data-testid="corporatePaymentItem:Receipt">
              Due On Receipt
            </MenuItem>
            <MenuItem value={PaymentTerm.Net15} data-testid="corporatePaymentItem:15">
              Net 15
            </MenuItem>
            <MenuItem value={PaymentTerm.Net30} data-testid="corporatePaymentItem:30">
              Net 30
            </MenuItem>
            <MenuItem value={PaymentTerm.Net45} data-testid="corporatePaymentItem:45">
              Net 45
            </MenuItem>
            <MenuItem value={PaymentTerm.Net60} data-testid="corporatePaymentItem:60">
              Net 60
            </MenuItem>
          </FormSelect>
        </Grid>
        <Grid item md={3} xs={12}>
          <FormTextField
            name="billId"
            label="Bill ID"
            defaultValue={account?.billId}
            fullWidth
            validationOptions={{
              maxLength: 3,
              minLength: 3,
              required: false,
            }}
            data-testid="corporateBillIdInput"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormNumberField
            name="automaticNoShowDays"
            label="No Show Hold Days"
            fullWidth
            defaultValue={account?.automaticNoShowDays ?? undefined}
            validationOptions={{
              min: {
                value: 1,
                message: 'Value must be greater than zero.',
              },
            }}
            helperText={
              errors.automaticNoShowDays?.message ??
              'Number of days held/charged prior to cancellation after the check-in date.'
            }
            data-testid="corporateNoShowHoldInput"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <FormTimePicker
            disableToolbar
            defaultValue={account.automaticNoShowTime}
            name="automaticNoShowTime"
            label="No Show Scheduled At Time"
            autoOk
            fullWidth
            ampm={false}
            format="HH:mm"
            clearable
            validationOptions={AUTO_NO_SHOW_TIME_VALIDATION}
          />
          <Typography variant="caption" color="textSecondary" data-testid="autoNoShowCutoffNote">
            {(errors as any).automaticNoShowTime?.message ??
              cutoffError?.message ??
              AUTO_NO_SHOW_CUTOFF_NOTE}
          </Typography>
        </Grid>
        <Grid item md={6} xs={12}>
          <FormCheckbox
            name="allowTaxCredit"
            label="Allow Tax Credit"
            defaultChecked={account?.allowTaxCredit}
            fullWidth
            helperText={
              'If enabled, then the corporate account will be eligible to receive tax credit'
            }
            disabled={account?.isGroupChild}
            data-testid="corporateAllowTaxCreditCheckbox"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          {showTaxCreditSplit && (
            <FormNumberField
              name="taxCreditSplit"
              label="Property Tax Credit Split"
              defaultValue={account.taxCreditSplit}
              validationOptions={{
                required: showTaxCreditSplit,
                min: 0,
                max: 100,
              }}
              onChangeCapture={(e: any) => setPropertyTaxCreditSplitValue(e.target.value)}
              helperText={errors => {
                if (account.isGroupChild === true) {
                  return 'Changes to Property Tax Credit Split must be made on the parent account.';
                }

                const creditSplitRemainder = propertyTaxCreditSplitValue
                  ? 100 - propertyTaxCreditSplitValue
                  : 100;
                const creditSplitMessage = `${propertyTaxCreditSplitValue}% will go to the property while ${creditSplitRemainder}% will go to the corporate account.`;
                return errors.taxCreditSplit?.type === 'required'
                  ? 'This field is required.'
                  : errors.taxCreditSplit?.type === 'min' || errors.taxCreditSplit?.type === 'max'
                  ? 'Enter values between 0 and 100'
                  : creditSplitMessage;
              }}
              disabled={account?.isGroupChild}
              fullWidth
              data-testid="corporateTaxCreditInput"
            />
          )}
        </Grid>
      </Grid>
      <Box py={1}>
        <Feedback
          show={status === FormStatus.Success}
          severity="success"
          onHide={reset}
          data-testid="billingSettingsSuccessDisplay"
        >
          Billing Settings saved successfully!
        </Feedback>
        <ErrorDisplay error={cutoffError ? undefined : error} />
      </Box>
    </>
  );
}
